import { Fragment } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ScoringAdminApi } from '../adminApi.js'
import type { ScheduledTask, ScoringRun, ScoringRunStatus } from '../index.js'
import { formatDateTime, EndpointHint, Skeleton } from '../../core/index.js'
import { Play } from 'lucide-react'
import { Button } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ScoringAdminApi

const TASK_LABELS: Record<ScheduledTask, string> = { recalculate: 'Recalculate running scores', 'close-periods': 'Close ended periods' }
const TASK_SCHEDULE: Record<ScheduledTask, string> = { recalculate: 'every day at 02:00 UTC', 'close-periods': 'every day at 00:30 UTC' }
const STATUS_CLASS: Record<ScoringRunStatus, string> = { succeeded: 'bg-green/15 text-green border-green/40', partial: 'bg-gold-lt text-ink border-gold/60', failed: 'bg-coral-lt text-coral border-coral/40' }

function duration(run: ScoringRun): string {
  const seconds = Math.max(0, Math.round((Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 100) / 10)
  return `${seconds}s`
}

/**
 * What the scheduler has done and a way to do it now: the two daily
 * tasks, every recent run with what it covered, and any facility a run
 * could not score.
 */
export function ScoringRunsPage({ api }: { api: AdminApi }) {
  const queryClient = useQueryClient()
  const runsQuery = useQuery({ queryKey: ['admin', 'scoring-runs'], queryFn: api.listScoringRuns })
  const runNow = useMutation({
    mutationFn: (task: ScheduledTask) => api.runScoringTask(task),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })

  return (
    <div className="px-6 py-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-5">
        <p className="max-w-2xl text-xs leading-relaxed text-ink-mid">
          EventBridge Scheduler invokes the API twice a day with a task. Each run walks every onboarded client's facilities; a facility that fails is
          recorded and the rest carry on. Run either task now to see it happen, or to catch up after a deploy.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          {(Object.keys(TASK_LABELS) as ScheduledTask[]).map((task) => (
            <div key={task} className="bg-white border border-marine/15 rounded p-4 flex flex-col gap-2">
              <div className="text-sm font-black text-ink">{TASK_LABELS[task]}</div>
              <div className="text-[11px] text-ink-mid">
                Scheduled {TASK_SCHEDULE[task]}. <span className="font-mono">{`{"source":"autonome.scheduler","task":"${task}"}`}</span>
              </div>
              <Button icon={Play} disabled={runNow.isPending} onClick={() => runNow.mutate(task)} className="self-start">
                {runNow.isPending && runNow.variables === task ? 'Running…' : 'Run now'}
              </Button>
            </div>
          ))}
        </div>
        {runNow.isError && <p className="text-xs font-bold text-coral">{runNow.error instanceof Error && runNow.error.message ? runNow.error.message : 'The run failed.'}</p>}

        <div className="bg-white border border-marine/15 rounded p-4">
          <div className="text-xs font-extrabold text-ink mb-2">
            Recent runs <EndpointHint>GET /admin/scoring/runs</EndpointHint>
          </div>
          {runsQuery.isPending ? (
            <Skeleton label="Loading runs" />
          ) : runsQuery.isError ? (
            <LoadFailure size="xs" message="Could not load the runs." onRetry={() => void runsQuery.refetch()} retrying={runsQuery.isFetching} />
          ) : runsQuery.data.length === 0 ? (
            <div className="text-[11px] text-ink-mid">No runs yet. The first scheduled run happens tonight; run a task now to see one.</div>
          ) : (
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
                  <th className="py-1 pr-2 font-bold">Started</th>
                  <th className="py-1 pr-2 font-bold">Task</th>
                  <th className="py-1 pr-2 font-bold">By</th>
                  <th className="py-1 pr-2 font-bold">Status</th>
                  <th className="py-1 pr-2 font-bold text-right">Clients</th>
                  <th className="py-1 pr-2 font-bold text-right">Facilities</th>
                  <th className="py-1 pr-2 font-bold text-right">Workers</th>
                  <th className="py-1 pr-2 font-bold text-right">Closed</th>
                  <th className="py-1 font-bold text-right">Took</th>
                </tr>
              </thead>
              <tbody>
                {runsQuery.data.map((run) => (
                  <Fragment key={run.runId}>
                    <tr className="border-t border-marine/10 text-ink">
                      <td className="py-1.5 pr-2 whitespace-nowrap">{formatDateTime(run.startedAt)}</td>
                      <td className="py-1.5 pr-2">{TASK_LABELS[run.task]}</td>
                      <td className="py-1.5 pr-2 text-ink-mid">{run.triggeredBy}</td>
                      <td className="py-1.5 pr-2">
                        <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold border rounded ${STATUS_CLASS[run.status]}`}>{run.status}</span>
                      </td>
                      <td className="py-1.5 pr-2 text-right">{run.clients}</td>
                      <td className="py-1.5 pr-2 text-right">{run.facilities}</td>
                      <td className="py-1.5 pr-2 text-right">{run.workers}</td>
                      <td className="py-1.5 pr-2 text-right">{run.task === 'close-periods' ? run.periodsClosed : '—'}</td>
                      <td className="py-1.5 text-right text-ink-mid">{duration(run)}</td>
                    </tr>
                    {run.errors.length > 0 && (
                      <tr>
                        <td colSpan={9} className="pb-2 pl-2 text-[10px] text-coral">
                          {run.errors.map((error) => `${error.clientId} / ${error.facilityId}: ${error.message}`).join(' · ')}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
